 /**
  * Centralized site structure for consistent navigation across the site
  * Used by Header, Footer, Sitemap generation, and internal linking
  */
 
 export interface SiteLink {
   label: string;
   href: string;
   description?: string;
 }
 
 export interface NavSection {
   label: string;
   href: string;
   children?: SiteLink[];
 }
 
 // Main navigation structure
export const mainNavigation: NavSection[] = [
  {
    label: "Products",
    href: "/products",
    children: [
      { label: "Selective Racking", href: "/products/selective", description: "Roll-Formed, Structural & Tubular" },
      { label: "Cantilever Racking", href: "/products/cantilever", description: "Long and bulky items" },
      { label: "VNA Racking", href: "/products/vna", description: "Narrow aisles, full selectivity" },
      { label: "Double-Deep Racking", href: "/products/double-deep", description: "Twice the density, standard parts" },
      { label: "Drive-In Systems", href: "/products/drive-in", description: "High density, LIFO storage" },
      { label: "Drive-Through Systems", href: "/products/drive-through", description: "Deep lane FIFO storage" },
      { label: "Push-Back Racking", href: "/products/push-back", description: "Dynamic, gravity-fed storage" },
      { label: "Pallet Flow Racking", href: "/products/pallet-flow", description: "Automatic FIFO gravity flow" },
      { label: "Mobile Racking", href: "/products/mobile", description: "Compacting rows, max density" },
      { label: "Shuttle Racking", href: "/products/shuttle", description: "Semi-automated deep lanes" },
      { label: "AS/RS Systems", href: "/products/asrs", description: "Fully automated storage" },
      { label: "Clad Rack Buildings", href: "/products/clad-rack", description: "Rack-supported structures" },
      { label: "Stack Racks", href: "/products/stack-racks", description: "Portable stackable frames" },
      { label: "Post Pallet Racks", href: "/products/post-pallets", description: "Removable post frames" },
    ],
  },
  {
    label: "Services",
    href: "/services",
    children: [
      { label: "New Racking", href: "/services/new", description: "Brand new from manufacturers" },
      { label: "Used Racking", href: "/services/used", description: "Quality inspected, 40-60% savings" },
      { label: "Sell Your Racking", href: "/services/sell", description: "Get top dollar for surplus" },
      { label: "Installation", href: "/services/installation", description: "Professional crew nationwide" },
      { label: "Inspection", href: "/services/inspection", description: "RMI-certified assessments" },
    ],
  },
  {
    label: "Resources",
    href: "/resources",
    children: [
      { label: "Rack Compatibility Guide", href: "/resources/rack-compatibility", description: "Identify your rack type" },
      { label: "Safety Standards", href: "/resources/safety", description: "RMI & ANSI guidelines" },
      { label: "OSHA Guidelines", href: "/resources/osha", description: "Compliance requirements" },
      { label: "Inspection Guide", href: "/resources/inspection-guide", description: "Complete checklist" },
      { label: "Layout Tips", href: "/resources/layout", description: "Optimize your warehouse" },
      { label: "Cost Estimator", href: "/tools/estimator", description: "Estimate your investment" },
      { label: "Warehouse Draw App", href: "/tools/warehouse-draw", description: "Design layouts in minutes" },
    ],
  },
 { label: "How It Works", href: "/how-it-works" },
 { label: "About", href: "/about" },
 {
   label: "Contact",
   href: "/contact",
   children: [
     { label: "Contact Us", href: "/contact", description: "Get in touch with our team" },
     { label: "All Locations", href: "/locations", description: "Nationwide coverage" },
     { label: "Utah", href: "/locations/utah", description: "Western US & Mountain Region" },
     { label: "North Carolina", href: "/locations/north-carolina", description: "East Coast & Southeast" },
     { label: "Ontario", href: "/locations/ontario", description: "Canada & Great Lakes" },
   ],
 },
];

// Footer link sections
export const footerLinks = {
  products: [
    { label: "Selective Racking", href: "/products/selective" },
    { label: "Cantilever Racking", href: "/products/cantilever" },
    { label: "VNA Racking", href: "/products/vna" },
    { label: "Double-Deep Racking", href: "/products/double-deep" },
    { label: "Drive-In Systems", href: "/products/drive-in" },
    { label: "Drive-Through Systems", href: "/products/drive-through" },
    { label: "Push-Back Racking", href: "/products/push-back" },
    { label: "Pallet Flow Racking", href: "/products/pallet-flow" },
    { label: "Mobile Racking", href: "/products/mobile" },
    { label: "Shuttle Racking", href: "/products/shuttle" },
    { label: "AS/RS Systems", href: "/products/asrs" },
    { label: "Clad Rack Buildings", href: "/products/clad-rack" },
    { label: "Stack Racks", href: "/products/stack-racks" },
    { label: "Post Pallet Racks", href: "/products/post-pallets" },
  ],
  services: [
    { label: "New Racking", href: "/services/new" },
    { label: "Used Racking", href: "/services/used" },
    { label: "Sell Your Racking", href: "/services/sell" },
    { label: "Installation", href: "/services/installation" },
    { label: "Inspection Services", href: "/services/inspection" },
  ],
  resources: [
    { label: "Rack Compatibility Guide", href: "/resources/rack-compatibility" },
    { label: "Safety Standards", href: "/resources/safety" },
    { label: "OSHA Guidelines", href: "/resources/osha" },
    { label: "Rack Inspection Guide", href: "/resources/inspection-guide" },
    { label: "Cost Estimator", href: "/tools/estimator" },
    { label: "Warehouse Layout Tips", href: "/resources/layout" },
    { label: "Warehouse Draw App", href: "/tools/warehouse-draw" },
  ],
  comparisons: [
    { label: "Drive-In vs Push-Back", href: "/compare/drive-in-vs-push-back" },
    { label: "Selective vs Double-Deep", href: "/compare/selective-vs-double-deep" },
    { label: "Pallet Flow vs Push-Back", href: "/compare/pallet-flow-vs-push-back" },
    { label: "Drive-In vs Shuttle", href: "/compare/drive-in-vs-shuttle" },
    { label: "Structural vs Roll-Formed", href: "/compare/structural-vs-roll-formed" },
    { label: "Mobile vs Drive-In", href: "/compare/mobile-vs-drive-in" },
  ],
  company: [
    { label: "About Us", href: "/about" },
    { label: "How It Works", href: "/how-it-works" },
    { label: "Locations", href: "/locations" },
    { label: "Contact", href: "/contact" },
    { label: "Privacy Policy", href: "/privacy" },
    { label: "Terms of Service", href: "/terms" },
  ],
};

// All routes for sitemap generation
export const allRoutes = [
 // Main pages
 { path: "/", priority: 1.0, changefreq: "weekly" },
 { path: "/quote", priority: 0.9, changefreq: "monthly" },
 { path: "/how-it-works", priority: 0.8, changefreq: "monthly" },
 { path: "/about", priority: 0.8, changefreq: "monthly" },
 { path: "/contact", priority: 0.8, changefreq: "monthly" },
 { path: "/booking", priority: 0.7, changefreq: "monthly" },
 { path: "/identify-rack", priority: 0.7, changefreq: "monthly" },
  
  // Products
  { path: "/products", priority: 0.9, changefreq: "weekly" },
  { path: "/products/selective", priority: 0.9, changefreq: "monthly" },
  { path: "/products/cantilever", priority: 0.8, changefreq: "monthly" },
  { path: "/products/vna", priority: 0.8, changefreq: "monthly" },
  { path: "/products/double-deep", priority: 0.8, changefreq: "monthly" },
  { path: "/products/drive-in", priority: 0.8, changefreq: "monthly" },
  { path: "/products/drive-through", priority: 0.8, changefreq: "monthly" },
  { path: "/products/push-back", priority: 0.8, changefreq: "monthly" },
  { path: "/products/pallet-flow", priority: 0.8, changefreq: "monthly" },
  { path: "/products/mobile", priority: 0.8, changefreq: "monthly" },
  { path: "/products/shuttle", priority: 0.8, changefreq: "monthly" },
  { path: "/products/asrs", priority: 0.8, changefreq: "monthly" },
  { path: "/products/clad-rack", priority: 0.8, changefreq: "monthly" },
  { path: "/products/stack-racks", priority: 0.7, changefreq: "monthly" },
  { path: "/products/post-pallets", priority: 0.7, changefreq: "monthly" },
  
  // Comparisons
  { path: "/compare/drive-in-vs-push-back", priority: 0.7, changefreq: "monthly" },
  { path: "/compare/selective-vs-double-deep", priority: 0.7, changefreq: "monthly" },
  { path: "/compare/pallet-flow-vs-push-back", priority: 0.7, changefreq: "monthly" },
  { path: "/compare/drive-in-vs-shuttle", priority: 0.7, changefreq: "monthly" },
  { path: "/compare/structural-vs-roll-formed", priority: 0.7, changefreq: "monthly" },
  { path: "/compare/mobile-vs-drive-in", priority: 0.7, changefreq: "monthly" },
  
  // Services
  { path: "/services", priority: 0.9, changefreq: "weekly" },
  { path: "/services/new", priority: 0.8, changefreq: "monthly" },
  { path: "/services/used", priority: 0.8, changefreq: "monthly" },
  { path: "/services/sell", priority: 0.8, changefreq: "monthly" },
  { path: "/services/installation", priority: 0.8, changefreq: "monthly" },
  { path: "/services/inspection", priority: 0.8, changefreq: "monthly" },
  
  // Locations
  { path: "/locations", priority: 0.8, changefreq: "monthly" },
  { path: "/locations/utah", priority: 0.7, changefreq: "monthly" },
  { path: "/locations/north-carolina", priority: 0.7, changefreq: "monthly" },
  { path: "/locations/ontario", priority: 0.7, changefreq: "monthly" },
  
  // Resources
  { path: "/resources", priority: 0.8, changefreq: "weekly" },
  { path: "/resources/safety", priority: 0.7, changefreq: "monthly" },
  { path: "/resources/osha", priority: 0.7, changefreq: "monthly" },
  { path: "/resources/inspection-guide", priority: 0.7, changefreq: "monthly" },
   { path: "/resources/layout", priority: 0.7, changefreq: "monthly" },
   { path: "/resources/rack-compatibility", priority: 0.8, changefreq: "monthly" },
   { path: "/tools/estimator", priority: 0.7, changefreq: "monthly" },
   { path: "/tools/warehouse-draw", priority: 0.8, changefreq: "monthly" },
  
  // Legal
  { path: "/privacy", priority: 0.3, changefreq: "yearly" },
  { path: "/terms", priority: 0.3, changefreq: "yearly" },
];